import { useParams, Navigate, Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

import { industries } from '@/data/industries';
import { subcategoryContent } from '@/data/subcategoryContent';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Factory, Package, Cog, FlaskConical, Wrench, CheckCircle2, ArrowLeft, Key } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import HoverTabSections, { type HoverTabSection } from '@/components/HoverTabSections';
import milkImage from '@/assets/dairy-products/milk-cartons.jpg';
import yoghurtImage from '@/assets/dairy-products/yoghurt.jpg';
import cheeseImage from '@/assets/dairy-products/cheese.jpg';
import butterImage from '@/assets/dairy-products/butter.jpg';
import iceCreamImage from '@/assets/dairy-products/ice-cream.jpg';
import waterImage from '@/assets/beverages/water.jpg';
import softDrinksImage from '@/assets/beverages/soft-drinks.jpg';
import juicesImage from '@/assets/beverages/juices.jpg';
import beerImage from '@/assets/beverages/beer.jpg';
import wineImage from '@/assets/beverages/wine.jpg';
import meatFishImage from '@/assets/meat-fish/meat-fish.jpg';
import eggsImage from '@/assets/meat-fish/eggs.jpg';
import oilsImage from '@/assets/oils-fats/oils.jpg';
import fatsImage from '@/assets/oils-fats/fats.jpg';

const subcategoryHeroBg: Record<string, string> = {
  'dairy-milk': milkImage,
  'dairy-yoghurt': yoghurtImage,
  'dairy-cheese': cheeseImage,
  'dairy-butter': butterImage,
  'dairy-ice-cream': iceCreamImage,
  'beverage-water': waterImage,
  'beverage-soft-drinks': softDrinksImage,
  'beverage-juices': juicesImage,
  'beverage-beer': beerImage,
  'beverage-wine': wineImage,
  'meat-fish-eggs-meat-fish': meatFishImage,
  'meat-fish-eggs-eggs': eggsImage,
  'oils-fats-oils': oilsImage,
  'oils-fats-fats': fatsImage,
};

const SubcategoryDetail = () => {
  const { industrySlug, subcategorySlug } = useParams();
  const { language, t } = useLanguage();
  
  const industry = industries.find(ind => ind.slug === industrySlug);
  const content = subcategoryContent[`${industrySlug}-${subcategorySlug}`];

  if (!industry || !content) {
    return <Navigate to="/industries" replace />;
  }

  const lang = language === 'el' ? 'gr' : 'en';

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={`${content.meta?.[`title_${lang}`] || ''} - ${industry.title[language]}`}
        description={content.meta?.[`description_${lang}`] || industry.description[language]}
        canonical={`https://www.tft.gr/industries/${industrySlug}/${subcategorySlug}`}
      />
      <Navigation />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-primary to-primary-light text-primary-foreground overflow-hidden">
          {subcategoryHeroBg[`${industrySlug}-${subcategorySlug}`] && (
            <div
              className="absolute inset-0 opacity-35 pointer-events-none [filter:grayscale(1)_contrast(1.1)] mix-blend-soft-light"
              style={{
                backgroundImage: `url(${subcategoryHeroBg[`${industrySlug}-${subcategorySlug}`]})`,
                backgroundRepeat: industrySlug === 'beverage' ? 'repeat' : 'no-repeat',
                backgroundSize: industrySlug === 'beverage' ? '320px auto' : 'cover',
                backgroundPosition: 'center',
              }}
              aria-hidden="true"
            />
          )}
          <div className="relative container mx-auto px-4">
            <Link to={`/industries/${industrySlug}`}>
              <Button variant="ghost" className="mb-4 text-primary-foreground hover:bg-primary-foreground/10">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('Πίσω στο', 'Back to')} {industry.title[language]}
              </Button>
            </Link>
            <h1 className="text-5xl font-bold mb-6">{content.meta[`title_${lang}`]}</h1>
            <p className="text-xl max-w-3xl opacity-90">
              {content.meta[`description_${lang}`]}
            </p>
          </div>
        </section>

        {/* Introduction */}
        {content.content[`intro_${lang}`] && (
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <Card>
                <CardContent className="p-8">
                  <p className="text-lg leading-relaxed text-muted-foreground">
                    {content.content[`intro_${lang}`]}
                  </p>
                </CardContent>
              </Card>
            </div>
          </section>
        )}

        {/* Solutions - Hover Tabs */}
        {(() => {
          const sols = content.content.solutions;
          const renderList = (items: string[], intro?: string) => (
            <>
              {intro && <p className="text-muted-foreground mb-4">{intro}</p>}
              <ul className="space-y-3">
                {items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </>
          );

          const sections: HoverTabSection[] = [];

          if (sols[`turnkey_${lang}`]) {
            sections.push({
              icon: Key,
              title: t('Turnkey', 'Turnkey'),
              content: (
                <p className="text-muted-foreground whitespace-pre-line">
                  {sols[`turnkey_${lang}`]}
                </p>
              ),
            });
          }
          if (sols[`process_${lang}`]) {
            sections.push({
              icon: Factory,
              title: t('Επεξεργασία', 'Process'),
              content: renderList(sols[`process_${lang}`], content.content[`overview_${lang}`]),
            });
          }
          if (sols[`packaging_${lang}`]) {
            sections.push({
              icon: Package,
              title: t('Συσκευασία', 'Packaging'),
              content: renderList(sols[`packaging_${lang}`], sols[`packaging_intro_${lang}`]),
            });
          }
          if (sols[`automation_${lang}`]) {
            sections.push({
              icon: Cog,
              title: t('Αυτοματισμοί', 'Automation'),
              content: renderList(sols[`automation_${lang}`], sols[`automation_intro_${lang}`]),
            });
          }
          if (sols[`lab_${lang}`]) {
            sections.push({
              icon: FlaskConical,
              title: t('Εργαστήριο', 'Lab & QC'),
              content: renderList(sols[`lab_${lang}`], sols[`lab_intro_${lang}`]),
            });
          }
          if (sols[`services_${lang}`]) {
            sections.push({
              icon: Wrench,
              title: t('Υπηρεσίες', 'Services'),
              content: renderList(sols[`services_${lang}`]),
            });
          }

          if (sections.length === 0) return null;
          return (
            <section className="py-16 bg-muted">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl font-bold mb-8 text-center">
                  {t('Λύσεις', 'Solutions')}
                </h2>
                <div className="max-w-5xl mx-auto">
                  <HoverTabSections sections={sections} />
                </div>
              </div>
            </section>
          );
        })()}

        {/* Benefits - Categorized */}
        {content.content[`benefits_categories_${lang}`] && (
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold mb-8 text-center">
                {t('Πλεονεκτήματα', 'Benefits')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {content.content[`benefits_categories_${lang}`].map((category: { title: string; items: string[] }, idx: number) => (
                  <Card key={idx} className="border-t-4 border-t-primary">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-bold mb-4">{category.title}</h3>
                      <ul className="space-y-3">
                        {category.items.map((item: string, itemIdx: number) => (
                          <li key={itemIdx} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <span className="text-muted-foreground text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Benefits - Simple list fallback */}
        {!content.content[`benefits_categories_${lang}`] && content.content[`benefits_${lang}`] && (
          <section className="py-16 bg-background">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold mb-8 text-center">
                {t('Πλεονεκτήματα', 'Benefits')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                {content.content[`benefits_${lang}`].map((benefit: string, idx: number) => (
                  <Card key={idx} className="border-l-4 border-l-primary">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                        <p className="text-muted-foreground">{benefit}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SubcategoryDetail;
